import { useCallback, useState } from "react"
import {
    Box,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { add, Dinero } from "dinero.js"
import { makeStyles } from "tss-react/mui"
import useActiveFiltersCountAccounting from "../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { TableSummaryWrap, TableTotalAmountBox } from "../../../../../shared/components/table/TableSummaryBox"
import { StandardFilterButton } from "../../../../../shared/components/table/StandardFilterButton"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { AccountingQuery, fetchProductSalesByPeriod } from "../accountingApi"
import { ExportButtonProductSales } from "./ExportButtonProductSales"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useOrderTypeFilter } from "../../../../../shared/components/filters/orderTypeFilter/useOrderTypeState"
import { StandardOrderTypeFilter } from "../../../../../shared/components/filters/orderTypeFilter/StandardOrderTypeFilter"
import {
    OrderFilterType,
    orderFilterButtonConfig,
} from "../../../../../shared/components/filters/orderTypeFilter/helpers"
import {
    StandardDateRangeFilter,
    type DtDateRange,
} from "../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        backgroundColor: theme.palette.common.white,
        ".MuiSelect-select": {
            padding: "0.85rem 1rem",
        },
    },
    footerCell: {
        padding: 0,
    },
    noResultCell: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[500],
        padding: "3rem 1rem",
    },
    dateFilterInput: {
        padding: "0.85rem 1rem",
    },
    dateRangeFilter: {
        width: "18rem",
        "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.common.white,
        },
    },
}))

const DATE_TYPE_OPTIONS_BY_ORDER_TYPE: Record<OrderFilterType, readonly DateType[]> = {
    [OrderFilterType.ALL]: [DateType.ORDER_DATE, DateType.DELIVERY_DATE],
    [OrderFilterType.POS]: [DateType.ORDER_DATE, DateType.DELIVERY_DATE],
    [OrderFilterType.MEETING_SERVICE]: [DateType.BOOKED_DATE, DateType.DELIVERY_DATE],
}

type ProductSalesTabProps = Readonly<{}>

export function ProductSalesTab({}: ProductSalesTabProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const { merchantId } = useMerchant()
    const { startDate, endDate } = useInitialDateFilters()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const { orderType, setOrderType } = useOrderTypeFilter()

    const dateTypeOptions = DATE_TYPE_OPTIONS_BY_ORDER_TYPE[orderType]

    const handleOrderTypeChange = useCallback(
        (value: OrderFilterType) => {
            setOrderType(value)
            const options = DATE_TYPE_OPTIONS_BY_ORDER_TYPE[value]
            setDateType((current) => (options.includes(current) ? current : options[0]))
        },
        [setOrderType],
    )

    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
        merchantId: merchantId.toString(),
    })

    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })

    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    const productSalesReport = useApiQuery(fetchProductSalesByPeriod, {
        queryName: AccountingQuery.PRODUCT_SALES,
        dependencies: {
            ...filtersList,
            merchantId,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
            dateType,
            orderType,
        },
    })

    const productGroups = productSalesReport.data?.productGroups ?? []

    const addMany = (addends: Dinero<number>[]) => addends.reduce(add)

    return (
        <>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap="1rem" width="100%" paddingBottom="2rem">
                <Box flexShrink={0}>
                    <StandardOrderTypeFilter
                        isMarginBottom={false}
                        orderType={orderType}
                        setOrderType={handleOrderTypeChange}
                        config={orderFilterButtonConfig}
                    />
                </Box>
                <SelectDateTypeFilter
                    dateType={dateType}
                    setDateType={setDateType}
                    options={dateTypeOptions}
                    className={classes.dateFilter}
                />

                <LocalizedStrict id="merchant-accounting-z-reports-table-date-range-filter" attrs={{ label: true }}>
                    <StandardDateRangeFilter
                        label="Date range"
                        inputClasses={classes.dateFilterInput}
                        dateFilterClasses={classes.dateRangeFilter}
                        value={dateState}
                        onChange={setDateState}
                    />
                </LocalizedStrict>

                <Stack direction="row" gap="1rem" marginLeft="auto">
                    <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} bordered />
                    <ExportButtonProductSales
                        data={productSalesReport.data}
                        startDate={dateState.startDate.toISODate()}
                        endDate={dateState.endDate.toISODate()}
                    />
                </Stack>
            </Stack>

            {productSalesReport.isLoading && <LoadingSpinner />}
            {!productSalesReport.isLoading && (
                <TableContainer component={Paper} sx={{ marginBottom: "5rem" }}>
                    <Table sx={{ minWidth: 500 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F8F8F8", textAlign: "center" }}>
                                <LocalizedStrict id="accounting-product-sale-product-code">
                                    <TableCell sx={{ width: "auto" }}>Produktkode</TableCell>
                                </LocalizedStrict>
                                <LocalizedStrict id="accounting-product-sale-product">
                                    <TableCell sx={{ width: "auto" }}> Produkter </TableCell>
                                </LocalizedStrict>
                                <LocalizedStrict id="accounting-product-sale-product-quantity">
                                    <TableCell sx={{ width: "auto", textAlign: "end" }}> Antal </TableCell>
                                </LocalizedStrict>

                                <LocalizedStrict id="accounting-product-sale-product-amount">
                                    <TableCell sx={{ width: "auto", textAlign: "end" }}> Beløb </TableCell>
                                </LocalizedStrict>
                            </TableRow>
                        </TableHead>

                        {productGroups.length === 0 && (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={4} className={classes.noResultCell}>
                                        <LocalizedStrict id="production-list-no-result">
                                            <Typography variant="body2">No result</Typography>
                                        </LocalizedStrict>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}

                        {productGroups.map((group, gIndex) => {
                            return (
                                <>
                                    {!group.productSales && (
                                        <Grid paddingBottom={"1rem"}>
                                            <LocalizedStrict id="production-list-no-result">
                                                <Typography
                                                    sx={{ fontSize: "14px", color: "#64748B", paddingBottom: "0.5rem" }}
                                                >
                                                    No result
                                                </Typography>
                                            </LocalizedStrict>
                                        </Grid>
                                    )}

                                    {group.productSales && (
                                        <>
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: "#F8F8F8", textAlign: "center" }}>
                                                    <TableCell sx={{ width: "175px" }}> </TableCell>
                                                    <TableCell sx={{ width: "auto", fontWeight: "bold" }}>
                                                        {" "}
                                                        {group.name}{" "}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ width: "auto", textAlign: "end", fontWeight: "bold" }}
                                                    >
                                                        {" "}
                                                        {group.productSales.reduce((acc, currentValue) => {
                                                            return acc + currentValue.numberOfOrders
                                                        }, 0)}{" "}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{ width: "250px", textAlign: "end", fontWeight: "bold" }}
                                                    >
                                                        {" "}
                                                        <>
                                                            {" "}
                                                            {moneyFactory.format(
                                                                addMany(
                                                                    group.productSales.map((product) => {
                                                                        return toDinero(product.amount)
                                                                    }),
                                                                ),
                                                            )}{" "}
                                                        </>{" "}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {group.productSales.map((product, pIndex) => {
                                                    return (
                                                        <>
                                                            <TableRow key={product.name + pIndex}>
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    sx={{ width: "auto" }}
                                                                >
                                                                    {product.code}
                                                                </TableCell>

                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    sx={{ width: "auto" }}
                                                                >
                                                                    {product.name}
                                                                </TableCell>

                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    sx={{ width: "auto", textAlign: "end" }}
                                                                >
                                                                    {product.numberOfOrders}
                                                                </TableCell>
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    sx={{ width: "auto", textAlign: "end" }}
                                                                >
                                                                    {moneyFactory.format(toDinero(product.amount))}
                                                                </TableCell>
                                                            </TableRow>
                                                        </>
                                                    )
                                                })}
                                            </TableBody>
                                        </>
                                    )}
                                </>
                            )
                        })}
                        {productGroups.length > 0 && (
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={4} className={classes.footerCell}>
                                        <TableSummaryWrap padding="sm">
                                            <TableTotalAmountBox
                                                data={productGroups.map((item) => item.productSales).flat()}
                                                accessor={(row) => row.amount}
                                            />
                                        </TableSummaryWrap>
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </TableContainer>
            )}
            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
